import type { ADFEntity } from '@atlaskit/adf-utils/types';
import { validator } from '@atlaskit/adf-utils/validator';
import type {
	ErrorCallbackOptions,
	Validate,
	ValidationError,
	ValidationErrorMap,
} from '@atlaskit/adf-utils/validatorTypes';
import type { Schema } from '@atlaskit/editor-prosemirror/model';

import { ACTION_SUBJECT_ID } from '../analytics';

export const UNSUPPORTED_NODE_ATTRIBUTE = 'unsupportedNodeAttribute';

import { ADFStages } from './ADFStages';
import { fireUnsupportedEvent } from './track-unsupported-content';
import type { UnsupportedContentPayload } from './unsupportedContent/types';
import type { ADFStage } from './validator';
import { wrapWithUnsupported } from './wrapWithUnsupported';
export type DispatchAnalyticsEvent = (event: UnsupportedContentPayload) => void;

const errorCallbackFor = (
	// Ignored via go/ees005
	// eslint-disable-next-line @typescript-eslint/no-explicit-any
	marks: any,
	validate: Validate,
	dispatchAnalyticsEvent?: DispatchAnalyticsEvent,
	validationOverrides?: {
		allowContainerInPanel?: boolean;
		allowNestedTables?: boolean;
		allowTableInPanel?: boolean;
	},
) => {
	return (entity: ADFEntity, error: ValidationError, options: ErrorCallbackOptions) => {
		return validationErrorHandler(
			entity,
			error,
			{
				...options,
				allowNestedTables: validationOverrides?.allowNestedTables,
				allowTableInPanel: validationOverrides?.allowTableInPanel,
				allowContainerInPanel: validationOverrides?.allowContainerInPanel,
			},
			marks,
			validate,
			dispatchAnalyticsEvent,
		);
	};
};

// eslint-disable-next-line @atlaskit/volt-strict-mode/no-multiple-exports
export const validationErrorHandler = (
	entity: ADFEntity,
	error: ValidationError,
	options: ErrorCallbackOptions,
	marks: string[],
	validate: Validate,
	dispatchAnalyticsEvent?: DispatchAnalyticsEvent,
):
	| ADFEntity
	| {
			attrs: {
				originalValue: ADFEntity;
			};
			type: string;
	  }
	| undefined => {
	if (entity && entity.type === UNSUPPORTED_NODE_ATTRIBUTE) {
		return entity;
	}

	if (options.isMark) {
		return wrapWithUnsupported(error.meta as ADFEntity, 'mark');
	}

	if (options.isNodeAttribute) {
		const entityType = entity && entity.type ? entity.type : undefined;
		return {
			type: UNSUPPORTED_NODE_ATTRIBUTE,
			attrs: {
				type: { nodeType: entityType },
				unsupported: error.meta,
			},
		};
	}

	if (entity && marks.indexOf(entity.type) > -1) {
		return;
	}

	/**
	 * There's a inconsistency between ProseMirror and ADF.
	 * `content` is actually optional in ProseMirror.
	 * And, also empty `text` node is not valid.
	 */
	if (error.code === 'MISSING_PROPERTIES' && entity.type === 'paragraph') {
		return { type: 'paragraph', content: [] };
	}

	// Ignored via go/ees007
	// eslint-disable-next-line @atlaskit/editor/enforce-todo-comment-format
	// TODO: We can repair missing content like `panel` without a `paragraph`.
	if (error.code === 'INVALID_CONTENT_LENGTH') {
		if (error.meta && options.allowUnsupportedBlock && entity.content) {
			return getEntityForInvalidContentLength(
				error,
				entity,
				validate,
				marks,
				dispatchAnalyticsEvent,
			);
		} else {
			// Can't fix it by wrapping
			if (dispatchAnalyticsEvent) {
				trackValidationError(dispatchAnalyticsEvent, error, entity);
			}
		}
	}

	// Nested tables don't actually exist in ADF. In ADF they are stored as an `extension` node with the nested table content serialized in a property of that extension.
	// Usually when we validate ADF we are validating this `extension` and then later transforming it into nested tables for usage by ProseMirror.
	// Under certain circumstances however, ADF validation is performed on this already transformed extension and since nested tables aren't allowed by ADF this
	// produces an error. For example, nested renderers in Confluence `bodiedExtension`'s. The nested renderer receives the already transformed nested tables that originated from the
	// `extension` node and validates it. This is invalid ADF so an error is thrown.
	// This override allows an exception to be made for nested tables in the validator in this circumstance.
	if (options.allowNestedTables) {
		const meta = error.meta as ValidationErrorMap['INVALID_CONTENT'] | undefined;
		if (
			options.allowNestedTables &&
			meta?.parentType &&
			['tableCell', 'tableHeader'].includes(meta?.parentType) &&
			error.code === 'INVALID_CONTENT' &&
			entity.type === 'table'
		) {
			return entity;
		}
	}

	// panel_c1 is a ProseMirror-only variant: on save, expand-in-panel documents are stored as
	// plain ADF `panel` nodes containing an `expand`. The base validator-spec does not permit
	// `expand` inside `panel` (it's gated behind the consolidated container-in-panel experiment,
	// see `isPanelNestingContainerSupported`), so we suppress the INVALID_CONTENT error when
	// `allowContainerInPanel` is set.
	if (options.allowContainerInPanel) {
		const meta = error.meta as ValidationErrorMap['INVALID_CONTENT'] | undefined;
		if (
			meta?.parentType === 'panel' &&
			error.code === 'INVALID_CONTENT' &&
			entity.type === 'expand'
		) {
			return entity;
		}
	}

	if (options.allowUnsupportedBlock) {
		return wrapWithUnsupported(entity);
	}

	if (options.allowUnsupportedInline) {
		return wrapWithUnsupported(entity, 'inline');
	}

	if (dispatchAnalyticsEvent) {
		trackValidationError(dispatchAnalyticsEvent, error, entity);
	}
	return entity;
};

function getEntityForInvalidContentLength(
	error: ValidationError,
	entity: ADFEntity,
	validate: Validate,
	marks: string[],
	dispatchAnalyticsEvent?: DispatchAnalyticsEvent,
): ADFEntity {
	const meta = error.meta as ValidationErrorMap['INVALID_CONTENT_LENGTH'];
	if (meta.type === 'maximum') {
		// Ignored via go/ees005
		// eslint-disable-next-line @typescript-eslint/no-non-null-assertion
		entity.content = entity
			.content!.filter((x): x is ADFEntity => !!x)
			.map((child, index) => {
				return index >= meta.requiredLength && child.type !== 'unsupportedBlock'
					? wrapWithUnsupported(child)
					: validate(child, errorCallbackFor(marks, validate, dispatchAnalyticsEvent)).entity;
			});
	}
	if (meta.type === 'minimum') {
		// Ignored via go/ees005
		// eslint-disable-next-line @typescript-eslint/no-non-null-assertion
		if (entity.content!.length === 0) {
			return wrapWithUnsupported(entity);
		}
		// Ignored via go/ees005
		// eslint-disable-next-line @typescript-eslint/no-non-null-assertion
		entity.content = entity
			.content!.filter((x): x is ADFEntity => !!x)
			.map((child) => {
				return child.type !== 'unsupportedBlock' ? wrapWithUnsupported(child) : child;
			});
	}
	return entity;
}

function trackValidationError(
	dispatchAnalyticsEvent: DispatchAnalyticsEvent,
	error: ValidationError,
	entity: ADFEntity,
) {
	if (!dispatchAnalyticsEvent) {
		return;
	}

	fireUnsupportedEvent(
		dispatchAnalyticsEvent,
		ACTION_SUBJECT_ID.UNSUPPORTED_ERROR,
		{
			type: entity.type || '',
			ancestry: entity.ancestorHierarchy || '',
			parentType: entity.parentType || '',
			marks: entity.marks || [],
			attrs: entity.attrs || {},
		},
		error.code,
	);
}

// eslint-disable-next-line @atlaskit/volt-strict-mode/no-multiple-exports
export const validateADFEntity = (
	schema: Schema,
	node: ADFEntity,
	dispatchAnalyticsEvent?: DispatchAnalyticsEvent,
	validationOverrides?: {
		allowContainerInPanel?: boolean;
		allowNestedTables?: boolean;
		allowTableInPanel?: boolean;
	},
	adfStage?: ADFStage,
): ADFEntity => {
	const nodes = Object.keys(schema.nodes);
	const marks = Object.keys(schema.marks);
	// Full-ADF strictness is opt-in: only a caller that names `final` gets it, and omitting `adfStage`
	// keeps stage-0 specs acceptable. Strictness has to be asked for rather than defaulted into,
	// because a document holding a stage-0-only construct, such as a `layoutSection` with one column,
	// otherwise has that content wrapped as unsupported. Editors carry such constructs by design,
	// since the editor's own schema enables them, and renderers meet them in stored documents.
	const validate = validator(nodes, marks, {
		allowPrivateAttributes: true,
		stage0: adfStage !== ADFStages.FINAL,
	});
	const emptyDoc: ADFEntity = { type: 'doc', content: [] };

	const { entity = emptyDoc } = validate(
		node,
		errorCallbackFor(marks, validate, dispatchAnalyticsEvent, validationOverrides),
	);

	return entity;
};
// eslint-disable-next-line @atlaskit/editor/no-re-export
export { wrapWithUnsupported } from './wrapWithUnsupported';
